import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewServerQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {createContext, use, useMemo} from 'react'

import {useNestedSubIssuesSubscription} from '../subscriptions/sub-issue-subscription'
import {applyIssueDependenciesSummaryDelta} from '../utils/optimistic-overlay'
import {fetchNestedSubIssues, nestedSubIssuesQueryKey} from './fetch-nested-sub-issues'
import type {SubIssuesListItemNode} from './sub-issues-list-item-query-types'

type IssueNode = NonNullable<NonNullable<IssueViewerSecondaryViewServerQueryQuery['repository']>['issue']>

// Fields read by the list and header, derived from the allowlisted secondary-view query type.
// Relay parents still supply this data in isolated consumers, so widen it with RelayCompat.
export type SubIssuesData = RelayCompat<
  Pick<
    IssueNode,
    | 'id'
    | 'databaseId'
    | 'number'
    | 'state'
    | 'stateReason'
    | 'subIssuesConnection'
    | 'subIssuesSummary'
    | 'title'
    | 'titleHTML'
    | 'url'
  > & {
    readonly repository: Pick<IssueNode['repository'], 'name' | 'nameWithOwner' | 'visibility' | 'viewerCanPush'> & {
      readonly owner: Pick<IssueNode['repository']['owner'], 'login'>
    }
    readonly parent: Pick<NonNullable<IssueNode['parent']>, 'id'> | null
    readonly subIssues: {
      readonly nodes: ReadonlyArray<SubIssuesListItemNode | null> | null
    }
  }
>

// `null` means there is no provider. A provider may intentionally supply `undefined` while its
// parent query is resolving; consumers must not mount a second query owner during that window.
export const SubIssuesDataContext = createContext<SubIssuesData | undefined | null>(null)

export function useSubIssuesDataContext(): SubIssuesData | undefined | null {
  return use(SubIssuesDataContext)
}

export function useSubIssuesData({
  issue,
  parentIssueId,
  owner,
  repo,
  number,
  initialData,
  updatesChannel,
  insideSidePanel,
}: {
  issue?: SubIssuesData
  parentIssueId: string
  owner: string
  repo: string
  number: number
  initialData?: SubIssuesListItemNestedSubIssuesServerQueryQuery
  updatesChannel?: string | null
  insideSidePanel?: boolean
}): SubIssuesData | undefined {
  const queryKey = useMemo(() => nestedSubIssuesQueryKey(owner, repo, number), [number, owner, repo])
  useNestedSubIssuesSubscription(updatesChannel, owner, repo, number)
  const {data} = useQuery({
    queryKey,
    queryFn: ({signal}) => fetchNestedSubIssues(owner, repo, number, signal),
    enabled: initialData !== undefined,
    initialData,
    staleTime: insideSidePanel ? 0 : Infinity,
  })
  const querySubIssues = data?.repository?.issue?.subIssues
  const issueWithNestedSubIssues = useMemo(
    () =>
      issue && querySubIssues
        ? {
            ...issue,
            subIssues: {nodes: querySubIssues.nodes},
            subIssuesConnection: {...issue.subIssuesConnection, totalCount: querySubIssues.totalCount},
          }
        : issue,
    [issue, querySubIssues],
  )
  const connections = useMemo(() => [{ownerId: parentIssueId, connectionPath: ['subIssues']}], [parentIssueId])
  const targets = useMemo(() => (data ? [{queryKey}] : []), [data, queryKey])

  return useOptimisticData(issueWithNestedSubIssues, {
    connections,
    targets,
    applyEntityFieldValuesDelta: applyIssueDependenciesSummaryDelta,
  })
}
